/**
 * The liquidity rule (Oct 2026). Backend source of truth:
 * app/frameworks/cc_score/liquidity_gate.py, served by GET /api/stocks/liquidity-rule.
 *
 * One rule everywhere: the screener's default filters, the card's "Spread" /
 * "Thin options" chips, and the analysis page's liquidity facts. A stock outside
 * the rule is flagged, never hidden by us; users can loosen the filters.
 * Inclusive: spread <= maxSpreadPct, open interest >= minOpenInterest.
 *
 * The app renders immediately with today's rule (the constants below, or the
 * last server rule cached in this browser). `loadLiquidityRule()` (fired once at
 * start-up, index.tsx, never awaited) swaps in the server rule when it arrives
 * and notifies subscribers (`useLiquidityRule`), so flipping the backend switch
 * LIQUIDITY_V2_ENABLED (phase 2c: median spread of the standard contracts vs mid,
 * re-calibrated threshold, "no standard contract" fails) moves the filters and
 * chips together with the score cap. Temporary rule (founder 2026-10-07, backend
 * liquidity_gate.LIQUIDITY_V2_UNQUOTED_FAILS): under v2 a stock whose standard
 * contracts are not quoted yet keeps an unknown spread ("—") but does NOT fail
 * the rule (`unquotedSpreadFails` false); only "no standard contract" fails.
 * The pass/fail itself comes from the backend. Read the fields at use time (never copy
 * them into a module-level constant) and call `useLiquidityRule()` in components
 * that render them.
 */
import { useSyncExternalStore } from 'react';
import config from '../config';

export interface LiquidityRule {
  maxSpreadPct: number;
  minOpenInterest: number;
  minStockVolume: number;
  /** 'v1' = tightest contract, (ask - bid) / ask; 'v2' = median of standard contracts, (ask - bid) / mid */
  version: 'v1' | 'v2';
  /** v2 only: true = an unquoted spread (standard contracts, none quoted) fails the rule; false = temporary rule, it does not */
  unquotedSpreadFails: boolean;
}

/** Today's rule (v1). */
export const DEFAULT_LIQUIDITY_RULE: Readonly<LiquidityRule> = Object.freeze({
  maxSpreadPct: 10,
  minOpenInterest: 50,
  minStockVolume: 100_000,
  version: 'v1',
  unquotedSpreadFails: false,
});

export const LIQUIDITY_RULE: LiquidityRule = { ...DEFAULT_LIQUIDITY_RULE };

/** Upper end of the open-interest slider; at the end means "no upper limit". */
export const OI_SLIDER_MAX = 2000;

const CACHE_KEY = 'babylon.liquidityRule';
let ruleVersion = 0;
const listeners = new Set<() => void>();

/** Apply a server rule; returns true when something changed (subscribers notified). */
export function applyLiquidityRule(r: Partial<{
  max_spread_pct: number; min_open_interest: number; min_stock_volume: number; version: string;
  unquoted_spread_fails: boolean;
}>): boolean {
  const next: LiquidityRule = { ...LIQUIDITY_RULE };
  if (typeof r.max_spread_pct === 'number') next.maxSpreadPct = r.max_spread_pct;
  if (typeof r.min_open_interest === 'number') next.minOpenInterest = r.min_open_interest;
  if (typeof r.min_stock_volume === 'number') next.minStockVolume = r.min_stock_volume;
  if (r.version === 'v1' || r.version === 'v2') next.version = r.version;
  if (typeof r.unquoted_spread_fails === 'boolean') next.unquotedSpreadFails = r.unquoted_spread_fails;
  const changed = (Object.keys(next) as (keyof LiquidityRule)[]).some((k) => next[k] !== LIQUIDITY_RULE[k]);
  if (!changed) return false;
  Object.assign(LIQUIDITY_RULE, next);
  ruleVersion += 1;
  listeners.forEach((fn) => fn());
  return true;
}

export function subscribeLiquidityRule(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Re-render when the server rule replaces the default; returns a version counter. */
export function useLiquidityRule(): number {
  return useSyncExternalStore(subscribeLiquidityRule, () => ruleVersion, () => ruleVersion);
}

// Start from the last server rule seen in this browser (if any): no flicker on reloads.
try {
  const cached = window.localStorage.getItem(CACHE_KEY);
  if (cached) applyLiquidityRule(JSON.parse(cached));
} catch {
  /* storage unavailable or bad JSON: keep today's rule */
}

/** Fetch the active rule in the background; keeps the current rule on any error. Never blocks rendering. */
export async function loadLiquidityRule(): Promise<void> {
  try {
    const res = await fetch(`${config.API_URL}/api/stocks/liquidity-rule`);
    if (!res.ok) return;
    const r = await res.json();
    try {
      window.localStorage.setItem(CACHE_KEY, JSON.stringify(r));
    } catch {
      /* ignore */
    }
    applyLiquidityRule(r);
  } catch {
    // keep the current rule (backend unreachable)
  }
}
